// frontend/src/pages/cargo/AcceptanceStep.js
import { useState, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { submitForApproval } from '../../store/slices/cargoSlice';

// =========================================================
// DATA EXTRACTION HELPERS (single source of truth)
// =========================================================
function firstNonEmpty(...values) {
    for (const v of values) {
        if (v !== null && v !== undefined && String(v).trim() !== '') {
            return String(v).trim();
        }
    }
    return '';
}

function resolveDestination(body) {
    if (!body) return 'N/A';
    const c = body.consignee?.[0] || body.consignee || {};
    return firstNonEmpty(
        c.destination_country,
        c.destinationCountry,
        c.country,
        body.destination_country,
        body.destinationCountry,
        body.to_location,
        body.voyage_to_country,
        body.port_of_discharge,
        body.delivery_place,
        c.address?.country,
        c.postal_address?.country,
        c.city,
        c.county,
    ) || 'N/A';
}

function resolveOrigin(body) {
    if (!body) return 'N/A';
    const c = body.consignee?.[0] || body.consignee || {};
    return firstNonEmpty(
        body.origin_country,
        body.originCountry,
        body.from_location,
        body.voyage_from_country,
        body.loading_at,
        c.country_of_origin,
        c.origin_country,
    ) || 'N/A';
}

function resolveIdfNumber(body) {
    if (!body) return 'N/A';
    return firstNonEmpty(body.idf_number, body.idfNumber, body.sadId) || 'N/A';
}

function resolveModeOfTransport(body) {
    if (!body) return 'N/A';
    return firstNonEmpty(body.mode_of_transport, body.modeOfTransport) || 'N/A';
}

// =========================================================
// CURRENCY FORMATTER
// =========================================================
function formatCurrency(amount, currency = 'KES') {
    const n = Number(amount || 0);
    if (!Number.isFinite(n)) return `${currency} 0.00`;
    return `${currency} ${n.toLocaleString('en-KE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

// =========================================================
// MAIN COMPONENT
// =========================================================
export default function AcceptanceStep({ quote, idfData, onComplete, onBack }) {
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [showConfirm, setShowConfirm] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // ---------- Derived (no state needed) ----------
    const body = idfData?.body || {};

    const destination = useMemo(() => resolveDestination(body), [body]);
    const origin = useMemo(() => resolveOrigin(body), [body]);
    const idfNumber = useMemo(() => resolveIdfNumber(body), [body]);
    const modeOfTransport = useMemo(() => resolveModeOfTransport(body), [body]);

    const currency = quote?.currency || 'KES';
    const quoteItems = quote?.items || [];
    const totalItems = quoteItems.length;

    // ---------- Missing data guard ----------
    if (!quote || !idfData?.body) {
        return (
            <div className="max-w-lg mx-auto text-center py-20">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-50 dark:bg-red-900/20 mb-4">
                    <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white">Missing Data</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                    Please go back and select a valid quotation.
                </p>
                {onBack && (
                    <button
                        onClick={onBack}
                        className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-semibold hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                    >
                        ← Back to Quotations
                    </button>
                )}
            </div>
        );
    }

    // ---------- Submit ----------
    const handleSubmitForApproval = useCallback(async () => {
        if (submitted || loading) return;

        setErrorMsg('');
        setLoading(true);

        try {
            const payload = buildAcceptPayload({
                quote,
                body,
                destination,
                origin,
            });

            if (!payload) throw new Error('Could not build the submission payload.');

            const result = await dispatch(submitForApproval(payload));

            if (submitForApproval.fulfilled.match(result)) {
                const responseData = result.payload || {};
                setSubmitted(true);
                onComplete({
                    ...responseData,
                    id: responseData?.db_id || responseData?.id || Date.now(),
                    quote_number: responseData?.quote_number || quote?.quoteNumber || 'N/A',
                    idf_number: idfNumber,
                    status: 'pending',
                    approval_status: 'pending',
                    submitted_at: new Date().toISOString(),
                });
            } else {
                setErrorMsg(result.error?.message || 'Submission failed. Please try again.');
            }
        } catch (err) {
            setErrorMsg(err?.message || 'Unexpected error while submitting.');
        } finally {
            setLoading(false);
            setShowConfirm(false);
        }
    }, [submitted, loading, quote, body, destination, origin, idfNumber, dispatch, onComplete]);

    return (
        <div className="max-w-5xl mx-auto animate-fadeIn space-y-6">

            {/* ========== HEADER ========== */}
            <div className="text-center space-y-3">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#2d5232] to-[#3a663f] shadow-2xl shadow-[#3a663f]/25 mb-3">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <h2 className="text-3xl md:text-4xl font-extrabold text-gray-800 dark:text-white tracking-tight">
                    Review &amp; Submit
                </h2>
                <p className="text-gray-500 dark:text-slate-400 max-w-md mx-auto">
                    Confirm your quotation details and send it for underwriter approval
                </p>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-full text-xs font-medium border border-amber-200 dark:border-amber-800">
                    <svg className="w-4 h-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Awaiting Underwriter Approval
                </div>
            </div>

            {/* ========== MAIN GRID ========== */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* ===== LEFT: Insurer + Premium + Items ===== */}
                <div className="lg:col-span-2 space-y-6">

                    {/* Insurer */}
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                        <div className="px-6 py-5 border-b border-gray-100 dark:border-slate-700 bg-gradient-to-r from-[#3a663f]/5 to-transparent dark:from-[#3a663f]/10">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl bg-[#3a663f]/10 dark:bg-[#3a663f]/20 flex items-center justify-center flex-shrink-0">
                                    <svg className="w-6 h-6 text-[#3a663f] dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                </div>
                                <div className="min-w-0">
                                    <h3 className="font-bold text-lg text-gray-800 dark:text-white truncate">
                                        {quote.name}
                                    </h3>
                                    <p className="text-xs text-gray-500 dark:text-slate-400 font-mono">
                                        KRA PIN: {quote.insuranceCompanyKraPin}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs uppercase tracking-wider text-gray-400 dark:text-slate-500">
                                        Basic Premium
                                    </p>
                                    <p className="text-lg font-bold text-gray-800 dark:text-white mt-1 tabular-nums">
                                        {formatCurrency(quote.basicPremium, currency)}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs uppercase tracking-wider text-gray-400 dark:text-slate-500">
                                        Total Premium
                                    </p>
                                    <p className="text-2xl font-extrabold text-[#3a663f] dark:text-green-400 mt-1 tabular-nums">
                                        {formatCurrency(quote.totalCharges, currency)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Premium Breakdown */}
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700">
                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                Premium Breakdown
                            </p>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                                <BreakdownTile label="Stamp Duty" value={formatCurrency(quote.stampDuty, currency)} />
                                <BreakdownTile label="Training Levy" value={formatCurrency(quote.trainingLevy, currency)} />
                                <BreakdownTile label="PHCF" value={formatCurrency(quote.phcf, currency)} />
                                <BreakdownTile label="MCI" value={formatCurrency(quote.mci, currency)} />
                                <BreakdownTile label="Total" value={formatCurrency(quote.totalCharges, currency)} highlight />
                            </div>
                        </div>
                    </div>

                    {/* Items */}
                    {totalItems > 0 && (
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
                                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    Covered Items ({totalItems})
                                </p>
                                <span className="text-xs text-gray-400 dark:text-slate-500 tabular-nums">
                                    {formatCurrency(quote.totalCharges, currency)}
                                </span>
                            </div>
                            <div className="p-4">
                                <div className="max-h-56 overflow-y-auto space-y-1">
                                    {quoteItems.map((item, idx) => (
                                        <div
                                            key={idx}
                                            className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <span className="w-6 h-6 rounded-full bg-[#3a663f]/10 dark:bg-[#3a663f]/20 flex items-center justify-center text-[10px] font-bold text-[#3a663f] dark:text-green-400 flex-shrink-0">
                                                    {idx + 1}
                                                </span>
                                                <span className="truncate text-sm text-gray-700 dark:text-gray-300">
                                                    {item.tariffGoodsDesc || item.tariff_goods_desc || `Item ${idx + 1}`}
                                                </span>
                                            </div>
                                            <span className="text-sm font-medium text-gray-800 dark:text-white whitespace-nowrap ml-4 tabular-nums">
                                                {formatCurrency(item.premium, currency)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* ===== RIGHT: Trip Details + Actions ===== */}
                <div className="space-y-6">

                    {/* Trip Details */}
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 bg-gradient-to-r from-blue-50 to-transparent dark:from-blue-900/10">
                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                </svg>
                                Trip Details
                            </p>
                        </div>
                        <div className="p-6 space-y-3">
                            <InfoRow label="IDF Number" value={idfNumber} mono />
                            <InfoRow label="Mode of Transport" value={modeOfTransport} />
                            <InfoRow label="Origin" value={origin} />
                            <InfoRow label="Destination" value={destination} accent="blue" />
                            <InfoRow label="Currency" value={currency} />
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                        <div className="p-6 space-y-4">
                            <button
                                onClick={() => setShowConfirm(true)}
                                disabled={loading || submitted}
                                className="w-full bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white font-semibold py-4 rounded-xl shadow-lg shadow-[#3a663f]/20 hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 transform active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#3a663f]/50 focus:ring-offset-2"
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                        </svg>
                                        Submit for Approval
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </>
                                )}
                            </button>

                            {onBack && !loading && !submitted && (
                                <button
                                    onClick={onBack}
                                    className="w-full text-center text-sm text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-white font-medium py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
                                >
                                    ← Back to Quotations
                                </button>
                            )}

                            {errorMsg && (
                                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl flex items-start gap-3 animate-fadeIn">
                                    <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="text-sm">{errorMsg}</span>
                                </div>
                            )}

                            <div className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-slate-700/30 rounded-xl">
                                <svg className="w-4 h-4 text-gray-400 dark:text-slate-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-xs text-gray-400 dark:text-slate-500 leading-relaxed">
                                    An underwriter will review this quotation. You'll receive a notification once approved — usually within a few minutes during business hours.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ========== CONFIRMATION MODAL ========== */}
            {showConfirm && (
                <ConfirmModal
                    quote={quote}
                    idfNumber={idfNumber}
                    origin={origin}
                    destination={destination}
                    currency={currency}
                    loading={loading}
                    onCancel={() => !loading && setShowConfirm(false)}
                    onConfirm={handleSubmitForApproval}
                />
            )}
        </div>
    );
}

// =========================================================
// SUB-COMPONENTS
// =========================================================
function BreakdownTile({ label, value, highlight }) {
    return (
        <div
            className={`rounded-xl p-4 text-center border ${highlight
                    ? 'bg-[#3a663f]/5 dark:bg-[#3a663f]/10 border-[#3a663f]/20 dark:border-[#3a663f]/30'
                    : 'bg-gray-50 dark:bg-slate-700/50 border-gray-100 dark:border-slate-600'
                }`}
        >
            <p
                className={`text-[10px] uppercase tracking-wider ${highlight
                        ? 'text-[#3a663f] dark:text-green-400'
                        : 'text-gray-400 dark:text-slate-500'
                    }`}
            >
                {label}
            </p>
            <p
                className={`text-sm mt-1 tabular-nums ${highlight
                        ? 'font-bold text-[#3a663f] dark:text-green-400'
                        : 'font-semibold text-gray-800 dark:text-white'
                    }`}
            >
                {value}
            </p>
        </div>
    );
}

function InfoRow({ label, value, mono, accent }) {
    const accentClass =
        accent === 'blue'
            ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800'
            : 'bg-gray-50 dark:bg-slate-700/30';
    const valueClass =
        accent === 'blue'
            ? 'text-blue-700 dark:text-blue-400 font-bold'
            : 'text-gray-800 dark:text-white font-medium';

    return (
        <div className={`flex items-center justify-between p-3 rounded-xl ${accentClass}`}>
            <span className="text-xs text-gray-500 dark:text-slate-400 flex-shrink-0">{label}</span>
            <span
                className={`text-sm truncate ml-3 ${valueClass} ${mono ? 'font-mono' : ''}`}
                title={value}
            >
                {value || 'N/A'}
            </span>
        </div>
    );
}

function ConfirmModal({ quote, idfNumber, origin, destination, currency, loading, onCancel, onConfirm }) {
    return (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn"
            onClick={onCancel}
        >
            <div
                className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl max-w-md w-full p-6 md:p-8 text-center space-y-5"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-amber-50 dark:bg-amber-900/20 mx-auto">
                    <svg className="w-10 h-10 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                </div>

                <div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                        Submit for Approval?
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-slate-400 mt-2">
                        This sends the quotation to an underwriter. You'll be able to proceed with payment once approved.
                    </p>
                </div>

                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 text-left space-y-2">
                    <SummaryRow label="Insurer" value={quote.name} />
                    <SummaryRow label="IDF Number" value={idfNumber} mono />
                    <SummaryRow label="Origin" value={origin} />
                    <SummaryRow label="Destination" value={destination} accent="blue" />
                    <div className="pt-2 mt-2 border-t border-gray-200 dark:border-slate-600">
                        <div className="flex justify-between items-baseline">
                            <span className="text-sm text-gray-500 dark:text-slate-400">
                                Total Premium
                            </span>
                            <span className="font-bold text-[#3a663f] dark:text-green-400 tabular-nums">
                                {formatCurrency(quote.totalCharges, currency)}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="flex gap-3 pt-2">
                    <button
                        onClick={onCancel}
                        disabled={loading}
                        className="flex-1 py-3 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-gray-300"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={loading}
                        className="flex-1 py-3 bg-[#3a663f] hover:bg-[#2d5232] text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-60 flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-[#3a663f]/50 focus:ring-offset-2"
                    >
                        {loading ? (
                            <>
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                Submitting...
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Confirm
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

function SummaryRow({ label, value, mono, accent }) {
    const valueClass =
        accent === 'blue'
            ? 'text-blue-700 dark:text-blue-400 font-medium'
            : 'text-gray-800 dark:text-white font-medium';

    return (
        <div className="flex justify-between text-sm items-center gap-3">
            <span className="text-gray-500 dark:text-slate-400 flex-shrink-0">{label}</span>
            <span className={`truncate ${valueClass} ${mono ? 'font-mono' : ''}`} title={value}>
                {value || 'N/A'}
            </span>
        </div>
    );
}

// =========================================================
// PAYLOAD BUILDER (uses pre-resolved values)
// =========================================================
function buildAcceptPayload({ quote, body, destination, origin }) {
    try {
        const items = (quote.items || []).map((item) => ({
            tariffGoodsDesc: item.tariffGoodsDesc || item.tariff_goods_desc || '',
            hsCode: item.hsCode || item.hs_code || '',
            customsValue: item.customsValue || item.customs_value || '',
            isItemNum: item.isItemNum || item.is_item_num || '',
            premium: item.premium || '',
            stampDuty: item.stampDuty || item.stamp_duty || '',
            id: item.id || quote.insuranceId,
            approval: null,
        }));

        return {
            sadId: body.idf_number || body.sadId || body.idfNumber || '',
            modeOfTransport: body.mode_of_transport || body.modeOfTransport || 'Sea',
            coverType: null,
            destinationCountry: destination === 'N/A' ? '' : destination,
            originCountry: origin === 'N/A' ? '' : origin,
            mciStatus: body.mci_status || body.mciStatus || '',
            totalCustomValues: body.total_custom_values || body.totalCustomValues || '',
            quotation: {
                insuranceId: quote.insuranceId,
                name: quote.name,
                insuranceCompanyKraPin: quote.insuranceCompanyKraPin,
                basicPremium: quote.basicPremium,
                totalCharges: quote.totalCharges,
                stampDuty: quote.stampDuty,
                trainingLevy: quote.trainingLevy,
                phcf: quote.phcf,
                mci: quote.mci,
                items,
            },
        };
    } catch (err) {
        console.error('[AcceptanceStep] Build payload error:', err);
        return null;
    }
}